import cn from 'classnames';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useBreakpoints } from '../../hooks/useBreakpoints';
import useClickOutside from '../../hooks/useClickOutside';
import { usePreventScroll } from '../../hooks/usePreventScroll';

import styles from './CustomSelect.module.css';
import { FrontPageModalFooter } from './frontPageModal/components/frontPageModalFooter';
import { FrontPageModalHeader } from './frontPageModal/components/frontPageModalHeader';

import { useCustomSelectKeyDown } from './hooks/useCustomSelectKeyDown';
import { TriggerButton } from './TriggerButton';

interface CustomSelectProps {
    value: string;
    onChange: (value: string) => void;
    triggerLabel?: string;
    slim?: boolean;
    relative?: boolean;
    options: Array<{
        value: string;
        label: string;
        labelPrefix?: string;
        icon?: ReactNode;
    }>;
    className?: string;
    error?: string;
    fullscreenTitle?: string;
    hideLabelOnSmallScreen?: boolean;
    dropDirection?: 'up' | 'down';
    fullWidth?: boolean;
    fitContent?: boolean;
    hideFooter?: boolean;
    triggerIcon?: ReactNode;
}

export const CustomSelect = ({
    value,
    onChange,
    options,
    triggerLabel,
    slim = false,
    relative = false,
    className,
    error,
    fullscreenTitle,
    hideLabelOnSmallScreen = true,
    dropDirection = 'down',
    fullWidth = false,
    fitContent = false,
    hideFooter = false,
    triggerIcon,
}: CustomSelectProps) => {
    const [open, setOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const selected = useMemo(() => options.find((opt) => opt.value === value), [value, options]);
    const isSmallScreen = useBreakpoints({ breakpoint: 'sm' });

    const closeDropdown = () => {
        setOpen(false);
        setSelectedIndex(-1);
    };

    const handleOptionSelect = useCallback(
        (value: string) => {
            onChange(value);
            if (!isSmallScreen) closeDropdown();
        },
        [onChange, isSmallScreen],
    );

    const listRef = useRef<HTMLUListElement | null>(null);
    const componentRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
    itemRefs.current = [];

    useClickOutside(!isSmallScreen ? componentRef : null, closeDropdown);
    usePreventScroll(open && isSmallScreen);

    const handleKeyDown = useCustomSelectKeyDown({
        options,
        selected: selectedIndex,
        setSelected: setSelectedIndex,
        onSelect: (opt) => handleOptionSelect(opt.value),
        closeDropdown: () => setOpen(false),
        itemRefs,
    });

    useEffect(() => {
        if (!open) return;
        const index = options.findIndex((opt) => opt.value === value);
        if (index < 0) return;
        setSelectedIndex(index);
        requestAnimationFrame(() => {
            listRef.current?.focus();
            if (isSmallScreen) {
                const el = itemRefs.current[index];
                if (el) {
                    setTimeout(() => {
                        if (el) {
                            const container = listRef.current;
                            const elTop = el.offsetTop;
                            const elBottom = elTop + el.offsetHeight;

                            const containerScrollTop = container?.scrollTop ?? 0;
                            const containerHeight = container?.clientHeight ?? 0;

                            if (elTop < containerScrollTop || elBottom > containerScrollTop + containerHeight) {
                                el.scrollIntoView({ block: 'center' });
                            }
                        }
                    }, 0);
                }
            }
        });
    }, [open, options, value, isSmallScreen]);

    useEffect(() => {
        if (!open || isSmallScreen || selectedIndex < 0) return;
        const el = itemRefs.current[selectedIndex];
        el?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex, open, isSmallScreen]);

    const dropdown = (
        <div
            ref={!isSmallScreen ? dropdownRef : undefined}
            className={cn(styles.dropdown, {
                [styles['dropdown--slim']]: slim,
                [styles['dropdown--no-label']]: !triggerLabel,
                [styles['dropdown--relative']]: relative,
                [styles['dropdown--direction-up']]: dropDirection === 'up',
            })}>
            <FrontPageModalHeader title={fullscreenTitle} onClose={closeDropdown} />
            <ul
                // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: ideally this should be a <select> element, but we need to support custom styling and icons, custom styling for <select> is coming, but will take time before it is fully supported
                role="listbox"
                tabIndex={0}
                ref={listRef}
                data-dropdown="true" // can be deleted if removed form date-picker footer
                onKeyDown={handleKeyDown}
                aria-activedescendant={selectedIndex >= 0 ? `custom-select-option-${selectedIndex}` : undefined}
                className={cn(styles['dropdown-list'], {
                    [styles['dropdown-list--slim']]: slim,
                })}>
                {options.map((opt, i) => (
                    // biome-ignore lint/a11y/useFocusableInteractive: see first comment
                    <li
                        key={opt.value}
                        id={`custom-select-option-${i}`}
                        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: see first comment
                        role="option"
                        ref={(el) => {
                            itemRefs.current[i] = el;
                        }}
                        aria-selected={i === selectedIndex}
                        className={cn(i === selectedIndex ? 's-bg-primary-subtle' : '', styles['dropdown-list__item'])}
                        onClick={() => handleOptionSelect(opt.value)}
                        onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleOptionSelect(opt.value);
                        }}>
                        {opt.icon}
                        {opt.label}
                    </li>
                ))}
            </ul>
            {!hideFooter && <FrontPageModalFooter onClose={closeDropdown} />}
        </div>
    );

    return (
        <div
            ref={componentRef}
            className={cn(styles.select, className, {
                [styles['select--relative']]: relative,
                [styles['select--slim']]: slim,
                [styles['select--full-width']]: fullWidth,
                [styles['select--fit-content']]: fitContent,
            })}>
            <TriggerButton
                triggerLabel={triggerLabel}
                hideLabelOnSmallScreen={hideLabelOnSmallScreen}
                text={(selected?.labelPrefix ?? '') + (selected?.label ?? '')}
                onTrigger={() => setOpen((prev) => !prev)}
                prefix={triggerIcon ?? (selected?.icon && <span aria-hidden>{selected.icon}</span>)}
                slim={slim}
                open={open}
                error={error}
                aria={{
                    label: triggerLabel,
                    hasPopup: 'listbox',
                }}
            />
            {open && (isSmallScreen ? createPortal(dropdown, document.body) : dropdown)}
        </div>
    );
};
